import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import './Login.scss'
import { useParamountAuth } from 'context/ParamountAuthContext'
import {
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
} from 'firebase/auth'
import { AppleIcon, GoogleIcon } from 'components/Icons'
import { VersionInfo } from 'components'
import { getInviteCode } from 'services/token.svc'

function Login({isSelfRegistration = false}) {
  const { handleSignIn, initialised, tokenGuts, auth } = useParamountAuth()
  const [signInButtonClicked, setSignInButtonClicked] = useState({
    type: '',
    clicked: false,
  })
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [isSignUp, setIsSignUp] = useState(false)
  const [isForgotPassword, setIsForgotPassword] = useState(false)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  const navigate = useNavigate()
  const isValidInviteToken = getInviteCode()

  const loginSuccess = (roles) => {
    if (isValidInviteToken) {
      navigate('/accept_invitation')
    } else if (!isValidInviteToken && roles.includes('Unregistered')) {
      if (isSelfRegistration)
        navigate('/self_register_company')
      else
        navigate('/unauthorised_role')
    } else {
      navigate('/clients')
    }
    setSignInButtonClicked({ type: '', clicked: false })
  }

  const googleLogin = () => {
    setSignInButtonClicked({ type: 'google', clicked: true })
    if (!auth) {
      console.error('auth is null - Firebase not initialised yet')
      setSignInButtonClicked({ type: '', clicked: false })
      return
    }
    const provider = new GoogleAuthProvider()
    signInWithPopup(auth, provider)
      .then(async (result) => {
        const idToken = await auth.currentUser.getIdToken()
        await handleSignIn('google', idToken).then((res) => {
          if (res) {
            const token = tokenGuts(res)
            loginSuccess(token.roles)
          }
        })
      })
      .catch((error) => {
        console.error(error)
      })
  }

  const appleLogin = () => {
    setSignInButtonClicked({ type: 'apple', clicked: true })
    if (!auth) {
      setSignInButtonClicked({ type: '', clicked: false })
      return
    }
    const provider = new OAuthProvider('apple.com')
    provider.addScope('email')
    provider.addScope('name')
    signInWithPopup(auth, provider)
      .then(async (result) => {
        const idToken = await auth.currentUser.getIdToken()
        handleSignIn('apple', idToken).then((res) => {
          if (res) {
            const token = tokenGuts(res)
            loginSuccess(token.roles)
          }
        })
      })
      .catch((error) => {
        // Handle Errors here.
        console.error(error)
        setSignInButtonClicked({ type: '', clicked: false })
      })
  }

  const handleForgotPassword = async (e) => {
    e.preventDefault()
    setError('')
    setSuccessMessage('')

    if (!email) {
      setError('Please enter your email address')
      return
    }

    try {
      await sendPasswordResetEmail(auth, email)
      setSuccessMessage('Password reset email sent! Check your inbox.')
      console.log('Password reset email sent to:', email)
    } catch (error) {
      console.error('Password reset error:', error)

      if (error.code === 'auth/user-not-found') {
        setError('No account found with this email address.')
      } else if (error.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.')
      } else {
        setError(error.message)
      }
    }
  }

  const handleEmailAuth = async (e) => {
    e.preventDefault()
    setError('')
    setSuccessMessage('')

    // Basic validation
    if (password.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }

    if (isSignUp && (!firstName.trim() || !lastName.trim())) {
      setError('Please enter your first name and last name')
      return
    }

    try {
      if (isSignUp) {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password)
        
        await updateProfile(userCredential.user, {
          displayName: `${firstName.trim()} ${lastName.trim()}`
        })
        
        console.log('Account created successfully with name:', `${firstName} ${lastName}`)
      } else {
        await signInWithEmailAndPassword(auth, email, password)
        console.log('Signed in successfully!')
      }
      const idToken = await auth.currentUser.getIdToken(true)
      handleSignIn('email', idToken).then((res) => {
        if (res) {
          const token = tokenGuts(res)
          loginSuccess(token.roles)
        }
      })
    } catch (error) {
      console.error('Email auth error:', error)

      // User-friendly error messages
      if (error.code === 'auth/email-already-in-use') {
        setError('This email is already registered. Please sign in instead.')
      } else if (error.code === 'auth/invalid-credential' || error.code === 'auth/user-not-found' || error.code === 'auth/invalid-login-credentials') {
        setError('Invalid email or password. Please check your credentials or sign up.')
      } else if (error.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.')
      } else if (error.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.')
      } else if (error.code === 'auth/weak-password') {
        setError('Password is too weak. Please use at least 6 characters.')
      } else {
        setError(error.message)
      }
    }
  }

  return (
    <div className="login-container">
      <img src="./paramountLogo.png" alt="paramount logo" />
      
      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {initialised ? (
        <>
          {!isForgotPassword && (
            <>
              <button
                onClick={googleLogin}
                aria-busy={
                  signInButtonClicked.type === 'google'
                    ? signInButtonClicked.clicked
                    : false
                }
                disabled={signInButtonClicked.type}
              >
                <GoogleIcon />
                Continue with Google
              </button>
              <button
                onClick={appleLogin}
                aria-busy={
                  signInButtonClicked.type === 'apple'
                    ? signInButtonClicked.clicked
                    : false
                }
                disabled={signInButtonClicked.type}
              >
                <AppleIcon />
                Continue with Apple
              </button>

              <div className="divider">
                <span>OR</span>
              </div>
            </>
          )}

          {isForgotPassword ? (
            <form onSubmit={handleForgotPassword} className="email-form">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <button type="submit" className="submit-button">
                Send Reset Email
              </button>
            </form>
          ) : (
            <form onSubmit={handleEmailAuth} className="email-form">
              {isSignUp && (
                <>
                  <input
                    type="text"
                    placeholder="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                </>
              )}
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button type="submit" className="submit-button">
                {isSignUp ? 'Sign Up' : 'Sign In'}
              </button>
            </form>
          )}

          <div className="toggle-mode">
            {isForgotPassword ? (
              <button
                onClick={() => {
                  setIsForgotPassword(false)
                  setError('')
                  setSuccessMessage('')
                }}
                className="link-button"
                type="button"
              >
                Back to Sign In
              </button>
            ) : (
              <>
                <button
                  onClick={() => {
                    setIsSignUp(!isSignUp)
                    setError('')
                    setSuccessMessage('')
                  }}
                  className="link-button"
                  type="button"
                >
                  {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
                </button>
                {!isSignUp && (
                  <>
                    <span className="separator">  </span>
                    <button
                      onClick={() => {
                        setIsForgotPassword(true)
                        setError('')
                        setSuccessMessage('')
                      }}
                      className="link-button"
                      type="button"
                    >
                      Forgot Password?
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </>
      ) : (
        []
      )}
      <VersionInfo />
    </div>
  )
}

export default Login
